import * as Yup from 'yup';
import { DESTINATION_TYPES } from '../../constants/destinationTypes';
import { DestinationConfigMetaData } from '../interface';
import { SNOWFLAKE_HOST_URL_REGEX, SnowflakeAuthenticationType } from './constants';
import { getSnowflakeValidationErrorMessage } from './error-message';
import SnowFlake from './Snowflake';

function checkFormikStateForError(siblingPath: string) {
  return {
    message: `test-${siblingPath}`,
    name: 'test-sibling-state',
    test() {
      if (this.parent?.[siblingPath]) {
        return this.createError({
          message: this.parent?.[siblingPath]
        });
      }
      return true;
    }
  };
}

function validateCreatableDropdownFieldValue(path, validateField) {
  return {
    message: `${path}-creatableFieldValue`,
    name: `test-dropdown-${path}`,
    test(value) {
      if (!this.parent?.[`${path}-dropdownInCreatableMode`]) {
        return true;
      }

      if (!value) {
        return false;
      }

      return validateField(value);
    }
  };
}

export const SNOWFLAKE_CONFIG: DestinationConfigMetaData = {
  formComponent: SnowFlake,
  initialValues: {
    authType: SnowflakeAuthenticationType.KEY_PAIR,
    accountUrl: '',
    accountName: '',
    region: null,
    user: '',
    password: '',
    privateKey: '',
    passphrase: '',
    warehouseName: '',
    databaseName: '',
    schemaName: '',
    loadedAt: DESTINATION_TYPES.SNOWFLAKE.populateLoadedAt,
    isTableTypeTransient: false,
    sanitizeName: false,
    useCustomerS3Stage: false,
    storageIntegrationName: '',
    customerS3Bucket: '',
    customerS3Prefix: '',
    customerS3Region: null,
    customerIamRoleArn: '',
    customerExternalId: ''
  },
  validationSchema: Yup.object({
    accountUrl: Yup.string()
      .trim()
      .required()
      .test(checkFormikStateForError('invalidAccountUrl'))
      .matches(SNOWFLAKE_HOST_URL_REGEX, {
        excludeEmptyString: true,
        message: () => getSnowflakeValidationErrorMessage()
      }),
    accountName: Yup.string().trim().required(),
    region: Yup.string().nullable(),
    user: Yup.string().trim().required().test(checkFormikStateForError('invalidUserName')),
    password: Yup.string().when('authType', {
      is: authType => authType === SnowflakeAuthenticationType.ACCESS_CREDENTIALS,
      then: () => Yup.string().trim().required().test(checkFormikStateForError('invalidPassword')),
      otherwise: () => Yup.string().trim()
    }),
    privateKey: Yup.string().when('authType', {
      is: authType => authType === SnowflakeAuthenticationType.KEY_PAIR,
      then: () => Yup.string().trim().required(),
      otherwise: () => Yup.string().trim()
    }),
    passphrase: Yup.string().trim(),
    'warehouse-creatableFieldValue': Yup.string()
      .nullable()
      .test(
        validateCreatableDropdownFieldValue(
          'warehouse',
          warehouse =>
            warehouse.length > 0 &&
            warehouse.length < 100 &&
            /^[a-zA-Z][a-zA-Z0-9$_]*$/g.test(warehouse)
        )
      ),
    warehouseName: Yup.string().trim().required(),
    'database-creatableFieldValue': Yup.string()
      .nullable()
      .test(
        validateCreatableDropdownFieldValue(
          'database',
          database =>
            database.length > 0 &&
            database.length < 100 &&
            /^[a-zA-Z][a-zA-Z0-9$_]*$/g.test(database)
        )
      ),
    databaseName: Yup.string().trim().required(),
    schemaName: Yup.string().trim().required(),
    useCustomerS3Stage: Yup.boolean(),
    storageIntegrationName: Yup.string().when('useCustomerS3Stage', {
      is: true,
      then: () => Yup.string().trim().required(),
      otherwise: () => Yup.string().trim()
    }),
    customerS3Bucket: Yup.string().when('useCustomerS3Stage', {
      is: true,
      then: () => Yup.string().trim().required(),
      otherwise: () => Yup.string().trim()
    }),
    customerS3Prefix: Yup.string().trim(),
    customerS3Region: Yup.string().when('useCustomerS3Stage', {
      is: true,
      then: () => Yup.string().nullable().required(),
      otherwise: () => Yup.string().nullable()
    }),
    customerIamRoleArn: Yup.string().when('useCustomerS3Stage', {
      is: true,
      then: () => Yup.string().trim().required(),
      otherwise: () => Yup.string().trim()
    }),
    customerExternalId: Yup.string().trim()
  }),
  fieldNameMapping: {
    accountName: 'account-name',
    region: 'region',
    user: 'db-user',
    password: 'db-password',
    privateKey: 'private-key',
    passphrase: 'private-key-passphrase',
    warehouseName: 'warehouse',
    databaseName: 'db-name',
    schemaName: 'schema-name',
    loadedAt: 'loaded-at',
    isTableTypeTransient: 'is-table-type-transient',
    sanitizeName: 'Sanitize Table/Column Names',
    useCustomerS3Stage: 'use-customer-s3-stage',
    storageIntegrationName: 'storage-integration-name',
    customerS3Bucket: 'customer-s3-bucket',
    customerS3Prefix: 'customer-s3-prefix',
    customerS3Region: 'customer-s3-region',
    customerIamRoleArn: 'customer-iam-role-arn',
    customerExternalId: 'customer-external-id'
  }
};
